import CHECK_LIST from '@/modules/gem-meter/constants/rulesConstants';
import type { CheckListItem } from './../types/types.d';
import type { MutationTree } from 'vuex';

import { initStoreData } from './state';
import type {
  ElementGroupState,
  GemMeterStandard,
  GemMeterState,
  ScreenType,
  StandardListPayload,
} from '../types/types';

export const MutationTypes = {
  UPDATE_ELEMENT_GROUP: 'UPDATE_ELEMENT_GROUP',
  UPDATE_STANDARD_LIST: 'UPDATE_STANDARD_LIST',
  OPEN_MODAL_CHECKLIST: 'OPEN_MODAL_CHECKLIST',
  SET_GROUP_ITEM: 'SET_GROUP_ITEM',
  SET_IS_INITIALIZE: 'SET_IS_INITIALIZE',
  RESET_ALL: 'RESET_ALL',
  CHANGE_MODAL_VISIBILITY: 'CHANGE_MODAL_VISIBILITY',
  SET_SCREEN_INITIALIZE_STATUS: 'SET_SCREEN_INITIALIZE_STATUS',
  SET_LOADING: 'SET_LOADING',
  RESET_GROUP_ITEM: 'RESET_GROUP_ITEM',
  CHANGE_GROUP_LOADING: 'CHANGE_GROUP_LOADING',
  CHANGE_PREVIOUS_SCREEN: 'CHANGE_PREVIOUS_SCREEN',
  UPDATE_STORE: 'UPDATE_STORE',
  SET_FONT_FAMILY_INCONSISTENT: 'SET_FONT_FAMILY_INCONSISTENT',
  SET_FONT_WEIGHT_INCONSISTENT: 'SET_FONT_WEIGHT_INCONSISTENT',
  SET_PREVIEW_INCONSISTENT: 'SET_PREVIEW_INCONSISTENT',
  SET_OPEN_LIBRARY_FROM_GEM_METER: 'SET_OPEN_LIBRARY_FROM_GEM_METER',
  UPDATE_PAGE_RULES: 'UPDATE_PAGE_RULES',
  UPDATE_GEMPAGES_IMAGE: 'UPDATE_GEMPAGES_IMAGE',
  ADD_GEMPAGES_IMAGE: 'ADD_GEMPAGES_IMAGE',
};

export const mutations: MutationTree<GemMeterState> = {
  [MutationTypes.SET_LOADING](state: GemMeterState, loading: boolean): void {
    state.loading = loading;
  },
  [MutationTypes.UPDATE_ELEMENT_GROUP](state: GemMeterState, payload: ElementGroupState) {
    state.elementGroups = payload;
  },
  [MutationTypes.SET_FONT_FAMILY_INCONSISTENT](state: GemMeterState, payload: string) {
    state.inconsistentData.fontFamily = payload;
  },
  [MutationTypes.SET_PREVIEW_INCONSISTENT](state: GemMeterState, payload: boolean) {
    state.inconsistentData.isPreview = payload;
  },
  [MutationTypes.SET_FONT_WEIGHT_INCONSISTENT](state: GemMeterState, payload: string) {
    state.inconsistentData.fontWeight = payload;
  },
  [MutationTypes.UPDATE_STANDARD_LIST](state: GemMeterState, payload: StandardListPayload) {
    if (state[payload.key]) {
      state[payload.key] = payload.standards;
    }

    return state;
  },
  [MutationTypes.OPEN_MODAL_CHECKLIST](state: GemMeterState, toggle: boolean) {
    state.openModal = toggle;
  },
  [MutationTypes.SET_GROUP_ITEM](state: GemMeterState, groupItem: GemMeterStandard) {
    state.groupItem = groupItem;
  },
  [MutationTypes.SET_IS_INITIALIZE](state: GemMeterState, isInitialize: boolean) {
    state.isInitialize = isInitialize;

    return state;
  },
  [MutationTypes.UPDATE_GEMPAGES_IMAGE](state: GemMeterState, images: any) {
    state.images = images;
    return state;
  },
  [MutationTypes.ADD_GEMPAGES_IMAGE](state: GemMeterState, image: any) {
    state.images = state.images.concat(image);
    return state;
  },
  [MutationTypes.SET_SCREEN_INITIALIZE_STATUS](
    state: GemMeterState,
    payload: { screenSize: ScreenType; isInit: boolean },
  ) {
    const { screenSize, isInit } = payload;
    state.initializeStatus[screenSize] = isInit;
  },
  [MutationTypes.RESET_ALL](state: GemMeterState) {
    const [initShopableStandards, initConvertableStandards] = initStoreData();
    state.shopableStandards = initShopableStandards;
    state.convertableStandards = initConvertableStandards;
    state.elementGroups = {
      lg: {},
      md: {},
      sm: {},
      xs: {},
    };
    state.metric = {
      lg: {
        errors: 0,
        fixed: 0,
        skipped: 0,
      },
      md: {
        errors: 0,
        fixed: 0,
        skipped: 0,
      },
      sm: {
        errors: 0,
        fixed: 0,
        skipped: 0,
      },
      xs: {
        errors: 0,
        fixed: 0,
        skipped: 0,
      },
    };
    state.pageElements = {};
    state.elementsOfRules = {};
    state.openModal = false;
    state.groupItem = null;
    state.groupLoading = false;
    return state;
  },
  [MutationTypes.CHANGE_MODAL_VISIBILITY](state: GemMeterState, isVisible: boolean) {
    state.openModal = isVisible;

    return state;
  },
  [MutationTypes.RESET_GROUP_ITEM](state: GemMeterState) {
    state.groupItem = null;

    return state;
  },
  [MutationTypes.CHANGE_GROUP_LOADING](state: GemMeterState, isLoading: boolean) {
    state.groupLoading = isLoading;

    return state;
  },
  [MutationTypes.CHANGE_PREVIOUS_SCREEN](state: GemMeterState, screenType: ScreenType) {
    state.previousScreen = screenType;

    return state;
  },
  [MutationTypes.UPDATE_STORE](state: GemMeterState, data: GemMeterState) {
    state.shopableStandards = data.shopableStandards ? data.shopableStandards : state.shopableStandards;
    state.convertableStandards = data.convertableStandards ? data.convertableStandards : state.convertableStandards;
    state.elementGroups = data.elementGroups ? data.elementGroups : state.elementGroups;
    state.metric = data.metric ? data.metric : state.metric;
    state.isInitialize = data.isInitialize ? data.isInitialize : state.isInitialize;
    state.initializeStatus = data.initializeStatus ? data.initializeStatus : state.initializeStatus;
    state.elementsOfRules = data.elementsOfRules ? data.elementsOfRules : state.elementsOfRules;
    state.pageElements = data.pageElements ? data.pageElements : state.pageElements;
    state.pageRules = data.pageRules ? data.pageRules : state.pageRules;
    return state;
  },
  [MutationTypes.SET_OPEN_LIBRARY_FROM_GEM_METER](state: GemMeterState, data: boolean) {
    state.openLibraryFromGemMeter = data;

    return state;
  },
  [MutationTypes.UPDATE_PAGE_RULES](state: GemMeterState, data: CheckListItem[]) {
    const set = new Set(state.pageRules.concat(data).map((item) => item.key));

    state.pageRules = Array.from(set).map((key) => CHECK_LIST[key]);
    return state;
  },
};
